/**
 * RecordingsLibrary — unified view of every studio_recordings row + every
 * video_projects.output_files entry for the tenant. Each row offers an
 * "Attach as official…" CTA that POSTs to the iter-352 publish endpoint.
 *
 * Mounted as a tab in ChurchModule and ChurchModule's sermon dialog area
 * — but designed generically so it drops anywhere with a tenantId.
 *
 * Two grouped sections:
 *   1. OBS Recordings (`studio_recordings`) — raw recordings, possibly with
 *      a transcription job ID attached.
 *   2. Rendered Clips (`video_projects.output_files`) — finished social cuts
 *      ready to publish to a sermon or event hero.
 *
 * The user can search by title and pick a "publish to" host (sermon or event)
 * before clicking attach.
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Film, Mic, Link2, Search, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';

export default function RecordingsLibrary({ tenantId, churchId, defaultModule = 'church_sermons' }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [module, setModule] = useState(defaultModule);
  const [hostId, setHostId] = useState('');

  const { data: projectsData, isLoading: lpr } = useQuery({
    queryKey: ['rl-projects', tenantId],
    queryFn: () => api.get('/video/projects', { params: { tenant_id: tenantId } }).then(r => r.data),
    enabled: !!tenantId,
  });
  const { data: recsData, isLoading: lrec } = useQuery({
    queryKey: ['rl-obs-recordings', tenantId],
    queryFn: () => api.get('/obs/recordings', { params: { tenant_id: tenantId } }).then(r => r.data),
    enabled: !!tenantId,
  });
  const { data: hostsData } = useQuery({
    queryKey: ['rl-hosts', module, tenantId, churchId],
    queryFn: async () => {
      if (module === 'church_sermons' && churchId) {
        const r = await api.get(`/churches/${churchId}/sermons`).catch(() => ({ data: { sermons: [] } }));
        return { hosts: (r.data?.sermons || []).map(s => ({ id: s.id, label: s.title || 'Untitled sermon' })) };
      }
      if (module === 'event') {
        const r = await api.get('/admin/events').catch(() => ({ data: { events: [] } }));
        return { hosts: (r.data?.events || r.data || []).map(e => ({ id: e.id || e._id, label: e.title || e.name })) };
      }
      return { hosts: [] };
    },
    enabled: !!module,
  });

  const publishMut = useMutation({
    mutationFn: ({ projectId, outputId }) =>
      api.post(`/video/projects/${projectId}/output/${outputId}/publish`, {
        module, host_id: hostId,
      }).then(r => r.data),
    onSuccess: () => {
      toast.success('Attached as official recording');
      qc.invalidateQueries({ queryKey: ['rl-projects'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Flatten outputs into one searchable list with parent project context.
  const allOutputs = useMemo(() => {
    const rows = [];
    for (const p of (projectsData?.projects || [])) {
      for (const o of (p.output_files || [])) {
        rows.push({ project: p, output: o });
      }
    }
    return rows;
  }, [projectsData]);

  const filteredOutputs = allOutputs.filter(({ project, output }) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (project.title || '').toLowerCase().includes(q)
        || (output.label || output.filename || '').toLowerCase().includes(q)
        || (output.social_format || '').toLowerCase().includes(q);
  });
  const filteredRecs = (recsData?.recordings || []).filter(r => {
    if (!search) return true;
    return (r.title || '').toLowerCase().includes(search.toLowerCase());
  });

  const hosts = hostsData?.hosts || [];

  return (
    <div className="space-y-4" data-testid="recordings-library">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Film className="h-4 w-4 text-fuchsia-600" /> Recordings Library
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="relative flex-1 min-w-0 sm:min-w-[180px]">
              <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by title / format" className="pl-7 h-9 text-sm" data-testid="rl-search" />
            </div>
            <Select value={module} onValueChange={(v) => { setModule(v); setHostId(''); }}>
              <SelectTrigger className="w-full max-w-[160px] h-9" data-testid="rl-module"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="church_sermons">Attach to sermon</SelectItem>
                <SelectItem value="event">Attach to event</SelectItem>
              </SelectContent>
            </Select>
            <Select value={hostId} onValueChange={setHostId} disabled={!hosts.length}>
              <SelectTrigger className="w-full max-w-[200px] h-9" data-testid="rl-host">
                <SelectValue placeholder={hosts.length ? 'Pick a target…' : 'No targets available'} />
              </SelectTrigger>
              <SelectContent>
                {hosts.map(h => <SelectItem key={h.id} value={h.id}>{h.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Rendered clips */}
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Rendered clips ({filteredOutputs.length})</p>
          {lpr ? <Loader2 className="h-4 w-4 animate-spin" /> :
            filteredOutputs.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-3 border border-dashed rounded" data-testid="rl-clips-empty">
                No rendered clips in this tenant's video library yet.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4" data-testid="rl-clips">
                {filteredOutputs.map(({ project, output }) => (
                  <Card key={output.id} className="hover:border-fuchsia-300 transition" data-testid={`rl-clip-${output.id}`}>
                    <CardContent className="p-3 flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{output.label || output.filename}</p>
                        <p className="text-xs text-slate-500 truncate">
                          {project.title} · {output.social_format || 'no format'} · {Math.round((output.size_bytes || 0) / 1024 / 1024)} MB
                        </p>
                      </div>
                      <Button size="sm" variant="outline" disabled={!hostId || publishMut.isPending}
                        onClick={() => publishMut.mutate({ projectId: project.id, outputId: output.id })}
                        data-testid={`rl-attach-${output.id}`}>
                        <Link2 className="h-3.5 w-3.5 mr-1" /> Attach
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )
          }

          {/* OBS raw recordings */}
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">OBS recordings ({filteredRecs.length})</p>
          {lrec ? <Loader2 className="h-4 w-4 animate-spin" /> :
            filteredRecs.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-3 border border-dashed rounded" data-testid="rl-recs-empty">
                No OBS recordings ingested yet. Use the Broadcast Dashboard ingest panel to upload one.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="rl-recs">
                {filteredRecs.map(r => (
                  <Card key={r.id} data-testid={`rl-rec-${r.id}`}>
                    <CardContent className="p-3 flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate flex items-center gap-1"><Mic className="h-3.5 w-3.5 text-slate-400" />{r.title}</p>
                        <p className="text-xs text-slate-500">
                          {r.link_module || 'studio_recording'} · {Math.round((r.size_bytes || 0) / 1024 / 1024)} MB
                          {r.transcription_job_id && <Badge variant="outline" className="ml-1 text-[10px]">transcribed</Badge>}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )
          }
        </CardContent>
      </Card>
    </div>
  );
}
